import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth, VerifyUserDomainResponse } from 'hooks/useAuth';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import * as Yup from 'yup';
import { passwordRegex } from '../constants';

export type RegistrationFormData = {
  confirmPassword: string;
  email: string;
  firstname: string;
  lastname: string;
  password: string;
  phoneNumber: string;
  primaryCompanyName: string;
  title: string;
};

const RegistrationSchema = Yup.object().shape({
  firstname: Yup.string()
    .required('Required field')
    .min(2, 'The name must contain at least 2 characters')
    .nullable(),
  lastname: Yup.string()
    .required('Required field')
    .min(3, 'The Last Name must contain at least 3 characters')
    .nullable(),
  email: Yup.string().required('Please enter your email address').email('Enter a valid email'),
  title: Yup.string().required('Required field'),
  primaryCompanyName: Yup.string()
    .required('Required field')
    .min(2, 'Company name must contain at least 2 characters'),
  password: Yup.string()
    .required('Required field')
    .min(6, 'Password must contain at least 6 characters')
    .max(20, 'Password must not be longer than 20 characters')
    .matches(
      passwordRegex,
      `Use a minimum length of 6 characters\n
      Use a maximum length of 20 characters\n
      Use a mix of alphabets and numbers\n
      Use at least one uppercase character\n`,
    )
    .nullable(),
  confirmPassword: Yup.string()
    .required('Required field')
    .oneOf([Yup.ref('password'), null], 'Password does not match'),
});
// `Use at least one uppercase letter (A-Z)\n
// Use at least one lowercase letter (a-z)\n
// Use at least one digit (0-9)\n
// Use at least one special character from the set [@$!%*?&]\n
// No sequential numbers (e.g., 012, 123, 234, etc.) are allowed
// `,

export function useSignupForm({ onSignup }: { onSignup: (values: RegistrationFormData) => void }) {
  const firstNameInputRef = useRef<HTMLInputElement | null>(null);
  const { state } = useLocation();
  const { verifyDomain } = useAuth();
  const [verifyDomainResponse, setVerifyDomainResponse] = useState<
    VerifyUserDomainResponse | undefined
  >(state?.verifyDomainResponse);
  const [showUnknownDomainModal, setShowUnknownDomainModal] = useState(false);
  const signupFormData = useForm<RegistrationFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(
      verifyDomainResponse
        ? RegistrationSchema
        : Yup.object().shape({
            email: Yup.string()
              .required('Please enter your email address')
              .email('Enter a valid email'),
          }),
    ),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: state?.email || '',
      phoneNumber: '',
      title: '',
      primaryCompanyName: verifyDomainResponse?.companyName || '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (verifyDomainResponse?.companyName) {
      signupFormData.setValue('primaryCompanyName', verifyDomainResponse.companyName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [verifyDomainResponse?.companyName]);

  const onSubmit = useCallback(
    async (values: RegistrationFormData) => {
      if (verifyDomainResponse) {
        onSignup(values);
      } else {
        /*
          DevNote: 
            This explicit promise is to help react-hook-form to set it's form submission state. Currently, react-hook-form isn't supporting manual mutation of form submission state.
        */
        const authPromise = new Promise((resolve) => {
          verifyDomain(values.email, {
            onError: (error) => {
              handleError(error);
              resolve(true);
            },
            onSuccess: ({ data }) => {
              if (typeof data === 'object') {
                if (data?.messageCode === 'NEW_USER') {
                  if (data.loginType?.length === 1 && data.loginType?.[0] === 'SSO') {
                    window.location.href = data?.redirectUrl || '';
                  } else {
                    setVerifyDomainResponse(data);
                    setTimeout(() => {
                      if (firstNameInputRef.current) {
                        firstNameInputRef.current.focus();
                      }
                    }, 0);
                  }
                } else {
                  toast.error('Username is already taken');
                }
              }
              resolve(true);
            },
          });
        });

        await authPromise;
      }
    },
    [verifyDomainResponse, firstNameInputRef],
  );

  const handleError = useCallback((error: any) => {
    if (error.response.data?.errorMessages?.[0]?.errorCode === "Company Domain doesn't exist") {
      setShowUnknownDomainModal(true);
    } else {
      toast.error(catchErrorMessage(error));
    }
  }, []);

  return {
    firstNameInputRef,
    showUnknownDomainModal,
    signupFormData,
    verifyDomainResponse,
    onSubmit,
    setShowUnknownDomainModal,
    setVerifyDomainResponse,
  };
}
